"use client";

import React from "react";
import { OrganismError } from "./organism-error";

/**
 * Per-organism error boundary.
 *
 * The canvas already catches a throw in an organism's own body by calling it
 * inside a try. This closes the two sites that cannot reach: a throw in a
 * CHILD the organism renders, and a throw in a `.client.tsx` leaf. Both were
 * measured against a running server before this existed — each produced an
 * HTTP 500 through Next's `global-error`, losing the whole page and its
 * chrome, not a degraded section.
 *
 * `componentDidCatch` has no server equivalent, so this has to be a Client
 * Component. One module, reused per organism: the chunk downloads once
 * regardless of how many sections a page has.
 *
 * ⚠ Fallback is `organism-error`, NOT `dds-error-display` — that one lazily
 * imports a Bit package, and v3 ships none.
 */

interface OrganismBoundaryProps {
  /** Blok type, for the fallback and the console line. */
  type: string;
  children: React.ReactNode;
}

interface OrganismBoundaryState {
  error: Error | null;
}

export class OrganismBoundary extends React.Component<
  OrganismBoundaryProps,
  OrganismBoundaryState
> {
  state: OrganismBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): OrganismBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error) {
    // console.error rather than the server logger: this runs in the browser,
    // and the logger's transports are Node-only. The server-side throw is
    // already logged by the canvas or by Next itself.

    console.error(`[OrganismBoundary] ${this.props.type} failed`, error?.message);
  }

  /* New children after a failure get one retry — the editor re-renders on every
     keystroke, so a fixed blok recovers. Healthy boundaries are untouched, unlike
     a remount key, which would also restart every working section's state. */
  componentDidUpdate(prevProps: OrganismBoundaryProps) {
    if (this.state.error && prevProps.children !== this.props.children) {
      this.setState({ error: null });
    }
  }

  render() {
    const { error } = this.state;
    const { type, children } = this.props;

    if (error) {
      return (
        <OrganismError
          detail={error.message}
          type={type}
        />
      );
    }

    return children;
  }
}

export default OrganismBoundary;
